import React, { useState, useMemo } from "react";
import {
  Calendar,
  Clock,
  User,
  Plus,
  Check,
  X,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Stethoscope,
  AlertTriangle,
  Trash2,
  Settings,
  List,
  LayoutGrid,
  CalendarDays,
} from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { useAgenda, Agendamento } from "../context/AgendaContext";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useUsuarios } from "../../usuarios/context/UsuariosContext";
import ConfiguracaoAgenda from "./ConfiguracaoAgenda";
import ProgramacaoMensal from "./ProgramacaoMensal";
import { gerarPDFAgendaDia } from "../services/gerarPDFAgenda";

// ============================================
// HELPERS
// ============================================
const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const DIAS_SEMANA_FULL = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];
const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const formatarDataBR = (data: string) => {
  if (!data) return "";
  const [ano, mes, dia] = data.split("-");
  return `${dia}/${mes}/${ano}`;
};

const getHojeISO = () => {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(h.getDate()).padStart(2, "0")}`;
};

const getCorStatus = (status: string) => {
  switch (status) {
    case "agendado":
      return { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af" };
    case "realizado":
      return { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" };
    case "nao_realizado":
      return { bg: "#fef2f2", border: "#fecaca", text: "#991b1b" };
    case "remarcado":
      return { bg: "#fffbeb", border: "#fde68a", text: "#92400e" };
    case "cancelado":
      return { bg: "#f9fafb", border: "#e5e7eb", text: "#6b7280" };
    default:
      return { bg: "#f9fafb", border: "#e5e7eb", text: "#6b7280" };
  }
};

const getLabelStatus = (status: string) => {
  switch (status) {
    case "agendado":
      return "Agendado";
    case "realizado":
      return "Realizado";
    case "nao_realizado":
      return "Não realizado";
    case "remarcado":
      return "Remarcado";
    case "cancelado":
      return "Cancelado";
    default:
      return status;
  }
};

export default function AbaAgenda() {
  const { usuarioLogado } = useAuth();
  const {
    agendamentos,
    agendamentosPorData,
    agendamentosPorSemana,
    agendamentosPorMes,
    agendamentosPorPaciente,
    adicionarAgendamento,
    marcarRealizado,
    marcarNaoRealizado,
    remarcarAgendamento,
    excluirAgendamento,
    getConfigProfissional,
    gerarVagasDia,
    loading,
  } = useAgenda();
  const { pacientes } = usePacientes();

  const isAdmin = usuarioLogado?.tipo === "administrador";
  const podeAgendar =
    isAdmin ||
    usuarioLogado?.categoria === "enfermeiro" ||
    usuarioLogado?.categoria === "administrativo";

  // Estados
  const [visao, setVisao] = useState<"diaria" | "semanal" | "mensal">("diaria");
  const [dataSelecionada, setDataSelecionada] = useState(getHojeISO());
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [buscaPaciente, setBuscaPaciente] = useState("");
  const [mostrarBusca, setMostrarBusca] = useState(false);
  const [remarcando, setRemarcando] = useState<string | null>(null);
  const [novaDataRemarcacao, setNovaDataRemarcacao] = useState("");
  const [novaHoraRemarcacao, setNovaHoraRemarcacao] = useState("");
  const [subAba, setSubAba] = useState<
    "agenda" | "programacao" | "configuracao"
  >("agenda");
  const [filtroProfissional, setFiltroProfissional] = useState<string>("todos");
  const { usuarios, carregarUsuarios } = useUsuarios();

  // Carregar lista de usuários ao montar
  React.useEffect(() => {
    if (usuarios.length === 0) {
      carregarUsuarios();
    }
  }, []);

  // Novo agendamento
  const [novoAgendamento, setNovoAgendamento] = useState({
    pacienteId: "",
    pacienteNome: "",
    profissionalId: "",
    profissionalNome: "",
    profissionalCategoria: "",
    data: dataSelecionada,
    hora: "08:00",
    tipo: "retorno" as "consulta" | "retorno" | "primeira_consulta",
    modalidade: "presencial" as "presencial" | "online",
    telefoneOnline: "",
    observacao: "",
  });

  // Profissionais que podem ter agenda
  const profissionaisComAgenda = useMemo(() => {
    return pacientes.length >= 0
      ? [] // Será preenchido com usuários do sistema
      : [];
  }, []);

  // ============================================
  // NAVEGAÇÃO DE DATAS
  // ============================================
  const navegar = (direcao: number) => {
    const d = new Date(dataSelecionada + "T00:00:00");
    if (visao === "diaria") d.setDate(d.getDate() + direcao);
    else if (visao === "semanal") d.setDate(d.getDate() + 7 * direcao);
    else d.setMonth(d.getMonth() + direcao);
    setDataSelecionada(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
    );
  };

  const irParaHoje = () => setDataSelecionada(getHojeISO());

  // ============================================
  // DADOS FILTRADOS
  // ============================================
  const agendamentosDia = useMemo(() => {
    let lista = agendamentosPorData(dataSelecionada);
    // Médicos/psicólogos/nutricionistas só veem seus próprios
    if (
      usuarioLogado &&
      !isAdmin &&
      ["medico", "psicologo", "Nutricionista"].includes(usuarioLogado.categoria)
    ) {
      lista = lista.filter((a) => a.profissionalId === usuarioLogado.id);
    }
    // Filtro por profissional selecionado
    if (filtroProfissional !== "todos") {
      lista = lista.filter((a) => a.profissionalId === filtroProfissional);
    }
    return lista.sort((a, b) => a.hora.localeCompare(b.hora));
  }, [
    dataSelecionada,
    agendamentosPorData,
    usuarioLogado,
    isAdmin,
    filtroProfissional,
  ]);

  const dataSelecionadaObj = new Date(dataSelecionada + "T00:00:00");
  const diaSemanaTexto = DIAS_SEMANA_FULL[dataSelecionadaObj.getDay()];
  const mesTexto = MESES[dataSelecionadaObj.getMonth()];

  // ============================================
  // BUSCA DE PACIENTES
  // ============================================
  const pacientesFiltrados = useMemo(() => {
    if (buscaPaciente.length < 2) return [];
    const termo = buscaPaciente.toLowerCase();
    return pacientes
      .filter((p: any) => p.nome?.toLowerCase().includes(termo))
      .slice(0, 8);
  }, [buscaPaciente, pacientes]);

  // ============================================
  // AÇÕES
  // ============================================
  const handleAdicionarAgendamento = async () => {
    if (!novoAgendamento.profissionalId) {
      alert("Selecione um profissional.");
      return;
    }
    const configProf = getConfigProfissional(novoAgendamento.profissionalId);
    if (!configProf) {
      alert(
        "Este profissional ainda não tem agenda configurada. Vá em Configuração e configure a agenda primeiro."
      );
      return;
    }
    if (
      !novoAgendamento.pacienteId ||
      !novoAgendamento.data ||
      !novoAgendamento.hora
    ) {
      alert("Preencha paciente, data e hora.");
      return;
    }
    const hoje = new Date().toISOString().split("T")[0];
    if (novoAgendamento.data < hoje) {
      alert(
        "Não é possível agendar em datas passadas. Selecione hoje ou uma data futura."
      );
      return;
    }
    try {
      await adicionarAgendamento({
        ...novoAgendamento,
        status: "agendado",
        agendadoPor: usuarioLogado?.id || "",
        agendadoPorNome: usuarioLogado?.nome || "",
      });
      setMostrarFormulario(false);
      setNovoAgendamento({
        pacienteId: "",
        pacienteNome: "",
        profissionalId: usuarioLogado?.id || "",
        profissionalNome: usuarioLogado?.nome || "",
        profissionalCategoria: usuarioLogado?.categoria || "",
        data: dataSelecionada,
        hora: "08:00",
        tipo: "retorno",
        modalidade: "presencial",
        telefoneOnline: "",
        observacao: "",
      });
    } catch (error) {
      console.error("Erro ao agendar:", error);
      alert("Erro ao criar agendamento.");
    }
  };

  const handleRemarcar = async (id: string) => {
    if (!novaDataRemarcacao || !novaHoraRemarcacao) {
      alert("Informe a nova data e hora.");
      return;
    }
    // Validar se profissional atende neste dia
    const agOriginal = agendamentos.find((a: any) => a.id === id);
    if (agOriginal) {
      const config = getConfigProfissional(agOriginal.profissionalId);
      if (config && config.tipoAgenda === "fixa") {
        const diaSemana = new Date(novaDataRemarcacao + "T00:00:00").getDay();
        const atendeNoDia = config.diasFixos?.some(
          (d) => d.diaSemana === diaSemana
        );
        if (!atendeNoDia) {
          const diasAtende = config.diasFixos
            ?.map(
              (d) =>
                [
                  "Domingo",
                  "Segunda",
                  "Terça",
                  "Quarta",
                  "Quinta",
                  "Sexta",
                  "Sábado",
                ][d.diaSemana]
            )
            .join(", ");
          alert(
            `${agOriginal.profissionalNome} não atende neste dia da semana. Dias de atendimento: ${diasAtende}`
          );
          return;
        }
      }
      const vagas = gerarVagasDia(
        agOriginal.profissionalId,
        novaDataRemarcacao
      );
      if (vagas.length > 0 && !vagas.includes(novaHoraRemarcacao)) {
        alert(
          `Horário ${novaHoraRemarcacao} não está disponível. Horários disponíveis: ${vagas.join(
            ", "
          )}`
        );
        return;
      }
    }
    try {
      await remarcarAgendamento(id, novaDataRemarcacao, novaHoraRemarcacao);
      setRemarcando(null);
      setNovaDataRemarcacao("");
      setNovaHoraRemarcacao("");
    } catch (error) {
      console.error("Erro ao remarcar:", error);
    }
  };

  // ============================================
  // SEMANA
  // ============================================
  const diasSemana = useMemo(() => {
    const inicio = new Date(dataSelecionada + "T00:00:00");
    const diaSem = inicio.getDay();
    inicio.setDate(inicio.getDate() - diaSem); // Ir para domingo
    const dias: { data: string; label: string; diaSem: string }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(inicio);
      d.setDate(d.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
      dias.push({
        data: iso,
        label: String(d.getDate()),
        diaSem: DIAS_SEMANA[i],
      });
    }
    return dias;
  }, [dataSelecionada]);

  // ============================================
  // MÊS
  // ============================================
  const diasMes = useMemo(() => {
    const d = new Date(dataSelecionada + "T00:00:00");
    const ano = d.getFullYear();
    const mes = d.getMonth();
    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes + 1, 0);
    const diasNoMes = ultimoDia.getDate();
    const inicioSemana = primeiroDia.getDay();

    const dias: { data: string; dia: number; foraDoMes: boolean }[] = [];

    // Dias do mês anterior
    for (let i = inicioSemana - 1; i >= 0; i--) {
      const dd = new Date(ano, mes, -i);
      dias.push({
        data: `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(dd.getDate()).padStart(2, "0")}`,
        dia: dd.getDate(),
        foraDoMes: true,
      });
    }

    // Dias do mês atual
    for (let i = 1; i <= diasNoMes; i++) {
      const iso = `${ano}-${String(mes + 1).padStart(2, "0")}-${String(
        i
      ).padStart(2, "0")}`;
      dias.push({ data: iso, dia: i, foraDoMes: false });
    }

    // Completar última semana
    while (dias.length % 7 !== 0) {
      const dd = new Date(
        ano,
        mes + 1,
        dias.length - diasNoMes - inicioSemana + 1
      );
      dias.push({
        data: `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(dd.getDate()).padStart(2, "0")}`,
        dia: dd.getDate(),
        foraDoMes: true,
      });
    }

    return dias;
  }, [dataSelecionada]);

  // ============================================
  // ESTILOS
  // ============================================
  const btnStyle: React.CSSProperties = {
    padding: "6px 12px",
    border: "none",
    borderRadius: theme.borderRadius.md,
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    fontFamily: theme.fonts.sans,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  if (loading) {
    return (
      <div
        style={{ textAlign: "center", padding: "48px", color: theme.gray[500] }}
      >
        Carregando agenda...
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "16px",
      }}
    >
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h2
          style={{
            fontSize: "18px",
            fontWeight: "700",
            color: theme.gray[800],
            fontFamily: theme.fonts.sans,
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Calendar size={20} />
          Agenda de Consultas
        </h2>
        {/* SUB-ABAS */}
        {podeAgendar && (
          <div style={{ display: "flex", gap: "2px" }}>
            <button
              onClick={() => setSubAba("agenda")}
              style={{
                padding: "4px 12px",
                background:
                  subAba === "agenda" ? theme.primary[600] : theme.gray[100],
                color: subAba === "agenda" ? "white" : theme.gray[600],
                border: "none",
                borderRadius: theme.borderRadius.sm,
                fontSize: "11px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              Agendamentos
            </button>
            <button
              onClick={() => setSubAba("configuracao")}
              style={{
                padding: "4px 12px",
                background:
                  subAba === "configuracao"
                    ? theme.primary[600]
                    : theme.gray[100],
                color: subAba === "configuracao" ? "white" : theme.gray[600],
                border: "none",
                borderRadius: theme.borderRadius.sm,
                fontSize: "11px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              Configuração
            </button>
            <button
              onClick={() => setSubAba("programacao")}
              style={{
                padding: "4px 12px",
                background:
                  subAba === "programacao"
                    ? theme.primary[600]
                    : theme.gray[100],
                color: subAba === "programacao" ? "white" : theme.gray[600],
                border: "none",
                borderRadius: theme.borderRadius.sm,
                fontSize: "11px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              Programação Mensal
            </button>
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: "8px",
            visibility: subAba === "agenda" ? "visible" : "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              background: theme.gray[100],
              borderRadius: theme.borderRadius.md,
              padding: "2px",
            }}
          >
            {[
              { id: "diaria", icon: List, label: "Dia" },
              { id: "semanal", icon: LayoutGrid, label: "Semana" },
              { id: "mensal", icon: CalendarDays, label: "Mês" },
            ].map((v) => (
              <button
                key={v.id}
                onClick={() => setVisao(v.id as any)}
                style={{
                  padding: "4px 10px",
                  background:
                    visao === v.id ? theme.primary[600] : "transparent",
                  color: visao === v.id ? "white" : theme.gray[600],
                  border: "none",
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "11px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <v.icon size={12} /> {v.label}
              </button>
            ))}
          </div>
          {podeAgendar && (
            <button
              onClick={() => {
                setNovoAgendamento((prev) => ({
                  ...prev,
                  data: dataSelecionada,
                }));
                setMostrarFormulario(!mostrarFormulario);
              }}
              style={{
                ...btnStyle,
                background: theme.primary[600],
                color: "white",
                padding: "8px 16px",
              }}
            >
              <Plus size={14} /> Novo Agendamento
            </button>
          )}
        </div>
      </div>
      {/* CONFIGURAÇÃO */}
      {subAba === "programacao" && podeAgendar && <ProgramacaoMensal />}
      {subAba === "configuracao" && podeAgendar && <ConfiguracaoAgenda />}

      {subAba === "agenda" && (
        <>
          {/* FILTRO POR PROFISSIONAL */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
              padding: "8px 12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
            }}
          >
            <User size={14} color={theme.gray[500]} />
            <span
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: theme.gray[600],
                fontFamily: theme.fonts.sans,
              }}
            >
              Profissional:
            </span>
            <select
              value={filtroProfissional}
              onChange={(e) => setFiltroProfissional(e.target.value)}
              style={{
                padding: "4px 8px",
                border: `1px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.sm,
                fontSize: "12px",
                fontFamily: theme.fonts.sans,
                flex: 1,
                maxWidth: "300px",
              }}
            >
              <option value="todos">Todos os profissionais</option>
              {usuarios
                .filter(
                  (u: any) =>
                    u.ativo &&
                    ["medico", "psicologo", "nutricionista"].includes(
                      u.categoria
                    )
                )
                .map((u: any) => (
                  <option key={u.id} value={u.id}>
                    {u.nome} — {u.categoria}
                  </option>
                ))}
            </select>
            {filtroProfissional !== "todos" && (
              <button
                onClick={() => setFiltroProfissional("todos")}
                style={{
                  padding: "3px 8px",
                  background: theme.gray[200],
                  color: theme.gray[600],
                  border: "none",
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "11px",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Limpar filtro
              </button>
            )}
          </div>

          {/* ============================================ */}
          {/* NAVEGAÇÃO DE DATA */}
          {/* ============================================ */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              padding: "8px 12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
            }}
          >
            <button
              onClick={() => navegar(-1)}
              style={{
                ...btnStyle,
                background: "transparent",
                color: theme.gray[600],
              }}
            >
              <ChevronLeft size={18} />
            </button>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: theme.gray[800],
                  fontFamily: theme.fonts.sans,
                }}
              >
                {visao === "diaria" &&
                  `${diaSemanaTexto}, ${dataSelecionadaObj.getDate()} de ${mesTexto}`}
                {visao === "semanal" &&
                  `Semana de ${formatarDataBR(
                    diasSemana[0]?.data
                  )} a ${formatarDataBR(diasSemana[6]?.data)}`}
                {visao === "mensal" &&
                  `${mesTexto} ${dataSelecionadaObj.getFullYear()}`}
              </div>
              {dataSelecionada !== getHojeISO() && (
                <button
                  onClick={irParaHoje}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: theme.primary[600],
                    fontSize: "11px",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  Ir para hoje
                </button>
              )}
            </div>
            <button
              onClick={() => navegar(1)}
              style={{
                ...btnStyle,
                background: "transparent",
                color: theme.gray[600],
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* ============================================ */}
          {/* FORMULÁRIO DE NOVO AGENDAMENTO */}
          {/* ============================================ */}
          {mostrarFormulario && podeAgendar && (
            <div
              style={{
                padding: "16px",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: theme.borderRadius.lg,
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: theme.primary[700],
                  fontFamily: theme.fonts.sans,
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Plus size={16} /> Novo Agendamento
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {/* PROFISSIONAL */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Profissional
                  </label>
                  <select
                    value={novoAgendamento.profissionalId}
                    onChange={(e) => {
                      const prof = usuarios.find(
                        (u: any) => u.id === e.target.value
                      );
                      if (prof) {
                        setNovoAgendamento((prev) => ({
                          ...prev,
                          profissionalId: prof.id,
                          profissionalNome: prof.nome,
                          profissionalCategoria: (prof as any).categoria || "",
                        }));
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: `1px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: "13px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <option value="">Selecione o profissional...</option>
                    {usuarios
                      .filter(
                        (u: any) =>
                          u.ativo &&
                          ["medico", "psicologo", "nutricionista"].includes(
                            u.categoria
                          )
                      )
                      .map((u: any) => (
                        <option key={u.id} value={u.id}>
                          {u.nome} — {u.categoria}
                        </option>
                      ))}
                  </select>
                </div>
                {/* BUSCA PACIENTE */}
                <div style={{ position: "relative", gridColumn: "1 / -1" }}>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Paciente
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div style={{ flex: 1, position: "relative" }}>
                      <input
                        type="text"
                        value={novoAgendamento.pacienteNome || buscaPaciente}
                        onChange={(e) => {
                          setBuscaPaciente(e.target.value);
                          setNovoAgendamento((prev) => ({
                            ...prev,
                            pacienteId: "",
                            pacienteNome: "",
                          }));
                        }}
                        placeholder="Buscar paciente por nome..."
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: `1px solid ${theme.gray[300]}`,
                          borderRadius: theme.borderRadius.sm,
                          fontSize: "13px",
                          fontFamily: theme.fonts.sans,
                          boxSizing: "border-box",
                        }}
                      />
                      {pacientesFiltrados.length > 0 &&
                        !novoAgendamento.pacienteId && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              background: "white",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.md,
                              boxShadow: theme.shadows.lg,
                              zIndex: 50,
                              maxHeight: "150px",
                              overflowY: "auto",
                            }}
                          >
                            {pacientesFiltrados.map((p: any) => (
                              <div
                                key={p.id}
                                onClick={() => {
                                  setNovoAgendamento((prev) => ({
                                    ...prev,
                                    pacienteId: p.id,
                                    pacienteNome: p.nome,
                                  }));
                                  setBuscaPaciente("");
                                }}
                                style={{
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                  fontFamily: theme.fonts.sans,
                                  borderBottom: `1px solid ${theme.gray[100]}`,
                                }}
                                onMouseOver={(e) =>
                                  (e.currentTarget.style.background =
                                    theme.gray[50])
                                }
                                onMouseOut={(e) =>
                                  (e.currentTarget.style.background = "white")
                                }
                              >
                                <strong>{p.nome}</strong>
                                {p.cpf && (
                                  <span
                                    style={{
                                      color: theme.gray[400],
                                      marginLeft: "8px",
                                    }}
                                  >
                                    {p.cpf}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                    {novoAgendamento.pacienteId && (
                      <button
                        onClick={() => {
                          setNovoAgendamento((prev) => ({
                            ...prev,
                            pacienteId: "",
                            pacienteNome: "",
                          }));
                          setBuscaPaciente("");
                        }}
                        style={{
                          ...btnStyle,
                          background: "#fee2e2",
                          color: "#dc2626",
                        }}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
                {/* DATA */}
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Data
                  </label>
                  <input
                    type="date"
                    value={novoAgendamento.data}
                    onChange={(e) =>
                      setNovoAgendamento((prev) => ({
                        ...prev,
                        data: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: `1px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: "13px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                {/* HORA — vagas disponíveis */}
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Horário
                  </label>
                  {novoAgendamento.profissionalId && novoAgendamento.data ? (
                    (() => {
                      const vagas = gerarVagasDia(
                        novoAgendamento.profissionalId,
                        novoAgendamento.data
                      );
                      if (vagas.length === 0) {
                        const configP = getConfigProfissional(
                          novoAgendamento.profissionalId
                        );
                        return (
                          <div
                            style={{
                              padding: "8px",
                              background: "#fef2f2",
                              border: "1px solid #fecaca",
                              borderRadius: theme.borderRadius.sm,
                              fontSize: "11px",
                              color: "#991b1b",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            {!configP
                              ? "Profissional sem agenda configurada"
                              : "Nenhuma vaga disponível neste dia"}
                          </div>
                        );
                      }
                      return (
                        <select
                          value={novoAgendamento.hora}
                          onChange={(e) =>
                            setNovoAgendamento((prev) => ({
                              ...prev,
                              hora: e.target.value,
                            }))
                          }
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: "13px",
                            fontFamily: theme.fonts.sans,
                            boxSizing: "border-box",
                          }}
                        >
                          <option value="">Selecione o horário...</option>
                          {vagas.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                      );
                    })()
                  ) : (
                    <div
                      style={{
                        padding: "8px",
                        background: theme.gray[50],
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "11px",
                        color: theme.gray[400],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Selecione profissional e data primeiro
                    </div>
                  )}
                </div>
                {/* TIPO */}
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Tipo
                  </label>
                  <select
                    value={novoAgendamento.tipo}
                    onChange={(e) =>
                      setNovoAgendamento((prev) => ({
                        ...prev,
                        tipo: e.target.value as any,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: `1px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: "13px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="retorno">Retorno</option>
                    <option value="primeira_consulta">Primeira Consulta</option>
                    <option value="consulta">Consulta</option>
                  </select>
                </div>
                {/* MODALIDADE */}
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Modalidade
                  </label>
                  <select
                    value={novoAgendamento.modalidade}
                    onChange={(e) =>
                      setNovoAgendamento((prev) => ({
                        ...prev,
                        modalidade: e.target.value as "presencial" | "online",
                        telefoneOnline:
                          e.target.value === "presencial"
                            ? ""
                            : prev.telefoneOnline,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: `1px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: "13px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="presencial">Presencial</option>
                    <option value="online">Online (WhatsApp)</option>
                  </select>
                </div>
                {/* TELEFONE ONLINE */}
                {novoAgendamento.modalidade === "online" && (
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Telefone (com DDD)
                    </label>
                    <input
                      type="tel"
                      value={novoAgendamento.telefoneOnline}
                      onChange={(e) =>
                        setNovoAgendamento((prev) => ({
                          ...prev,
                          telefoneOnline: e.target.value.replace(/[^\d]/g, ""),
                        }))
                      }
                      placeholder="Ex: 69999887766"
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: `1px solid ${theme.gray[300]}`,
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "13px",
                        fontFamily: theme.fonts.sans,
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                )}
                {/* OBSERVAÇÃO */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Observação (opcional)
                  </label>
                  <input
                    type="text"
                    value={novoAgendamento.observacao}
                    onChange={(e) =>
                      setNovoAgendamento((prev) => ({
                        ...prev,
                        observacao: e.target.value,
                      }))
                    }
                    placeholder="Ex: trazer exames, paciente em uso de sonda..."
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: `1px solid ${theme.gray[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: "13px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
              {/* BOTÕES */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginTop: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => setMostrarFormulario(false)}
                  style={{
                    ...btnStyle,
                    background: theme.gray[300],
                    color: theme.gray[700],
                  }}
                >
                  <X size={14} /> Cancelar
                </button>
                <button
                  onClick={handleAdicionarAgendamento}
                  style={{
                    ...btnStyle,
                    background: theme.primary[600],
                    color: "white",
                    padding: "8px 16px",
                  }}
                >
                  <Check size={14} /> Agendar
                </button>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* VISÃO DIÁRIA */}
          {/* ============================================ */}
          {visao === "diaria" && (
            <div>
              {agendamentosDia.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "48px",
                    background: theme.gray[50],
                    borderRadius: theme.borderRadius.lg,
                    color: theme.gray[400],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Calendar
                    size={40}
                    color={theme.gray[300]}
                    style={{ marginBottom: "8px" }}
                  />
                  <p style={{ fontSize: "14px" }}>
                    Nenhuma consulta agendada para este dia
                  </p>
                  {podeAgendar && (
                    <button
                      onClick={() => setMostrarFormulario(true)}
                      style={{
                        ...btnStyle,
                        background: theme.primary[600],
                        color: "white",
                        margin: "12px auto 0",
                      }}
                    >
                      <Plus size={14} /> Agendar Consulta
                    </button>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {/* CONTADOR + BOTÃO PDF */}
                  <div
                    style={{
                      fontSize: "12px",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                      marginBottom: "4px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>
                      {agendamentosDia.length} consulta
                      {agendamentosDia.length > 1 ? "s" : ""} agendada
                      {agendamentosDia.length > 1 ? "s" : ""}
                      {" — "}
                      {
                        agendamentosDia.filter((a) => a.status === "realizado")
                          .length
                      }{" "}
                      realizada
                      {agendamentosDia.filter((a) => a.status === "realizado")
                        .length !== 1
                        ? "s"
                        : ""}
                    </span>
                    <button
                      onClick={() =>
                        gerarPDFAgendaDia(
                          dataSelecionada,
                          agendamentosDia,
                          filtroProfissional !== "todos"
                            ? usuarios.find(
                                (u: any) => u.id === filtroProfissional
                              )?.nome
                            : undefined
                        )
                      }
                      style={{
                        padding: "4px 10px",
                        background: theme.gray[100],
                        color: theme.gray[600],
                        border: "none",
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "11px",
                        fontWeight: "600",
                        cursor: "pointer",
                        fontFamily: theme.fonts.sans,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FileDown size={12} /> Imprimir Agenda
                    </button>
                  </div>
                  {/* CARDS */}
                  {agendamentosDia.map((ag) => {
                    const cor = getCorStatus(ag.status);
                    return (
                      <div
                        key={ag.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px",
                          background: cor.bg,
                          border: `1px solid ${cor.border}`,
                          borderRadius: theme.borderRadius.md,
                          borderLeft: `4px solid ${cor.text}`,
                        }}
                      >
                        {/* HORA */}
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "800",
                            color: cor.text,
                            fontFamily: theme.fonts.sans,
                            minWidth: "50px",
                            textAlign: "center",
                          }}
                        >
                          {ag.hora}
                        </div>
                        {/* INFO */}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: theme.gray[800],
                              fontFamily: theme.fonts.sans,
                              marginBottom: "2px",
                            }}
                          >
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  `${window.location.origin}/prontuario/${ag.pacienteId}`,
                                  "_blank"
                                );
                              }}
                              style={{
                                cursor: "pointer",
                                color: theme.primary[700],
                                textDecoration: "underline",
                              }}
                              title="Abrir prontuário em nova aba"
                            >
                              {ag.pacienteNome}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: theme.gray[500],
                              fontFamily: theme.fonts.sans,
                              display: "flex",
                              gap: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <Stethoscope size={10} /> {ag.profissionalNome}
                            </span>
                            <span
                              style={{
                                background: cor.text + "20",
                                color: cor.text,
                                padding: "1px 6px",
                                borderRadius: "8px",
                                fontSize: "10px",
                                fontWeight: "600",
                              }}
                            >
                              {getLabelStatus(ag.status)}
                            </span>
                            {(ag as any).modalidade === "online" && (
                              <span
                                style={{
                                  background: "#dcfce7",
                                  color: "#166534",
                                  padding: "1px 6px",
                                  borderRadius: "8px",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                }}
                              >
                                📱 Online
                              </span>
                            )}
                            {(ag as any).modalidade === "online" &&
                              (ag as any).telefoneOnline && (
                                <a
                                  href={
                                    "https://wa.me/55" +
                                    (ag as any).telefoneOnline
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#25D366",
                                    fontWeight: "700",
                                    fontSize: "11px",
                                    textDecoration: "none",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "3px",
                                  }}
                                >
                                  {"📞 " +
                                    (ag as any).telefoneOnline.replace(
                                      /(\d{2})(\d{5})(\d{4})/,
                                      "($1) $2-$3"
                                    )}
                                </a>
                              )}
                            {ag.observacao && (
                              <span style={{ fontStyle: "italic" }}>
                                {ag.observacao}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* AÇÕES — 🔐 B76: desfecho (realizado/não realizado)
                            p/ quem gere a agenda OU o profissional DONO da
                            consulta; remarcar/cancelar só p/ quem gere
                            (espelho das firestore.rules) */}
                        {ag.status === "agendado" &&
                          (podeAgendar ||
                            ag.profissionalId === usuarioLogado?.id) && (
                          <div style={{ display: "flex", gap: "4px" }}>
                            <button
                              onClick={() => marcarRealizado(ag.id)}
                              title="Marcar como realizado"
                              style={{
                                ...btnStyle,
                                background: "#dcfce7",
                                color: "#166534",
                              }}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => marcarNaoRealizado(ag.id)}
                              title="Não realizado"
                              style={{
                                ...btnStyle,
                                background: "#fee2e2",
                                color: "#dc2626",
                              }}
                            >
                              <X size={14} />
                            </button>
                            {podeAgendar && (
                              <>
                                <button
                                  onClick={() => {
                                    setRemarcando(ag.id);
                                    setNovaDataRemarcacao("");
                                    setNovaHoraRemarcacao(ag.hora);
                                  }}
                                  title="Remarcar"
                                  style={{
                                    ...btnStyle,
                                    background: "#fef3c7",
                                    color: "#92400e",
                                  }}
                                >
                                  <RefreshCw size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Cancelar agendamento de ${ag.pacienteNome}?`
                                      )
                                    ) {
                                      excluirAgendamento(ag.id);
                                    }
                                  }}
                                  title="Cancelar agendamento"
                                  style={{
                                    ...btnStyle,
                                    background: "#fee2e2",
                                    color: "#dc2626",
                                  }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {ag.status === "nao_realizado" && podeAgendar && (
                          <div style={{ display: "flex", gap: "4px" }}>
                            <button
                              onClick={() => {
                                setRemarcando(ag.id);
                                setNovaDataRemarcacao("");
                                setNovaHoraRemarcacao(ag.hora);
                              }}
                              style={{
                                ...btnStyle,
                                background: "#fef3c7",
                                color: "#92400e",
                              }}
                            >
                              <RefreshCw size={12} /> Remarcar
                            </button>
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Cancelar agendamento de ${ag.pacienteNome}?`
                                  )
                                ) {
                                  excluirAgendamento(ag.id);
                                }
                              }}
                              style={{
                                ...btnStyle,
                                background: "#fee2e2",
                                color: "#dc2626",
                              }}
                            >
                              <Trash2 size={12} /> Cancelar
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* MODAL DE REMARCAÇÃO */}
                  {remarcando && (
                    <div
                      style={{
                        padding: "12px",
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: theme.borderRadius.md,
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <RefreshCw size={16} color="#92400e" />
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#92400e",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Remarcar para:
                      </span>
                      <input
                        type="date"
                        value={novaDataRemarcacao}
                        onChange={(e) => setNovaDataRemarcacao(e.target.value)}
                        style={{
                          padding: "4px 8px",
                          border: `1px solid ${theme.gray[300]}`,
                          borderRadius: theme.borderRadius.sm,
                          fontSize: "12px",
                        }}
                      />
                      <input
                        type="time"
                        value={novaHoraRemarcacao}
                        onChange={(e) => setNovaHoraRemarcacao(e.target.value)}
                        style={{
                          padding: "4px 8px",
                          border: `1px solid ${theme.gray[300]}`,
                          borderRadius: theme.borderRadius.sm,
                          fontSize: "12px",
                        }}
                      />
                      <button
                        onClick={() => handleRemarcar(remarcando)}
                        style={{
                          ...btnStyle,
                          background: "#f59e0b",
                          color: "white",
                        }}
                      >
                        <Check size={12} /> Confirmar
                      </button>
                      <button
                        onClick={() => setRemarcando(null)}
                        style={{
                          ...btnStyle,
                          background: theme.gray[300],
                          color: theme.gray[700],
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ============================================ */}
          {/* VISÃO SEMANAL */}
          {/* ============================================ */}
          {visao === "semanal" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "4px",
              }}
            >
              {diasSemana.map((dia) => {
                const agDia = agendamentosPorData(dia.data).filter((a) => {
                  if (
                    !(
                      isAdmin ||
                      ["enfermeiro", "administrativo"].includes(
                        usuarioLogado?.categoria || ""
                      )
                    )
                  ) {
                    if (a.profissionalId !== usuarioLogado?.id) return false;
                  }
                  if (
                    filtroProfissional !== "todos" &&
                    a.profissionalId !== filtroProfissional
                  )
                    return false;
                  return true;
                });
                const isHoje = dia.data === getHojeISO();
                const isSelecionado = dia.data === dataSelecionada;

                return (
                  <div
                    key={dia.data}
                    onClick={() => {
                      setDataSelecionada(dia.data);
                      setVisao("diaria");
                    }}
                    style={{
                      padding: "8px",
                      background: isHoje ? "#eff6ff" : "white",
                      border: `1px solid ${
                        isSelecionado ? theme.primary[500] : theme.gray[200]
                      }`,
                      borderRadius: theme.borderRadius.md,
                      cursor: "pointer",
                      minHeight: "100px",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.boxShadow = theme.shadows.sm)
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.boxShadow = "none")
                    }
                  >
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "600",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                        textAlign: "center",
                      }}
                    >
                      {dia.diaSem}
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: isHoje ? "800" : "600",
                        color: isHoje ? theme.primary[700] : theme.gray[800],
                        fontFamily: theme.fonts.sans,
                        textAlign: "center",
                        marginBottom: "6px",
                      }}
                    >
                      {dia.label}
                    </div>
                    {agDia.slice(0, 3).map((ag) => (
                      <div
                        key={ag.id}
                        style={{
                          fontSize: "9px",
                          padding: "2px 4px",
                          marginBottom: "2px",
                          background: getCorStatus(ag.status).bg,
                          borderRadius: "4px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontFamily: theme.fonts.sans,
                          color: getCorStatus(ag.status).text,
                        }}
                      >
                        {ag.hora} {ag.pacienteNome.split(" ")[0]}
                      </div>
                    ))}
                    {agDia.length > 3 && (
                      <div
                        style={{
                          fontSize: "9px",
                          color: theme.gray[400],
                          textAlign: "center",
                        }}
                      >
                        +{agDia.length - 3} mais
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ============================================ */}
          {/* VISÃO MENSAL */}
          {/* ============================================ */}
          {visao === "mensal" && (
            <div>
              {/* CABEÇALHO DOS DIAS */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: "2px",
                  marginBottom: "4px",
                }}
              >
                {DIAS_SEMANA.map((d) => (
                  <div
                    key={d}
                    style={{
                      textAlign: "center",
                      fontSize: "11px",
                      fontWeight: "700",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                      padding: "4px",
                    }}
                  >
                    {d}
                  </div>
                ))}
              </div>
              {/* GRID DO MÊS */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: "2px",
                }}
              >
                {diasMes.map((dia, i) => {
                  const agDia = agendamentosPorData(dia.data).filter((a) => {
                    if (
                      !(
                        isAdmin ||
                        ["enfermeiro", "administrativo"].includes(
                          usuarioLogado?.categoria || ""
                        )
                      )
                    ) {
                      if (a.profissionalId !== usuarioLogado?.id) return false;
                    }
                    if (
                      filtroProfissional !== "todos" &&
                      a.profissionalId !== filtroProfissional
                    )
                      return false;
                    return true;
                  });
                  const isHoje = dia.data === getHojeISO();

                  return (
                    <div
                      key={i}
                      onClick={() => {
                        setDataSelecionada(dia.data);
                        setVisao("diaria");
                      }}
                      style={{
                        padding: "4px",
                        minHeight: "60px",
                        background: dia.foraDoMes
                          ? theme.gray[50]
                          : isHoje
                          ? "#eff6ff"
                          : "white",
                        border: `1px solid ${
                          isHoje ? theme.primary[300] : theme.gray[200]
                        }`,
                        borderRadius: "4px",
                        cursor: "pointer",
                        opacity: dia.foraDoMes ? 0.4 : 1,
                      }}
                      onMouseOver={(e) => {
                        if (!dia.foraDoMes)
                          e.currentTarget.style.background = theme.gray[50];
                      }}
                      onMouseOut={(e) => {
                        if (!dia.foraDoMes)
                          e.currentTarget.style.background = isHoje
                            ? "#eff6ff"
                            : "white";
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: isHoje ? "800" : "500",
                          color: isHoje ? theme.primary[700] : theme.gray[600],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dia.dia}
                      </div>
                      {agDia.length > 0 && (
                        <div style={{ marginTop: "2px" }}>
                          <div
                            style={{
                              background: theme.primary[500],
                              color: "white",
                              fontSize: "9px",
                              fontWeight: "700",
                              borderRadius: "8px",
                              textAlign: "center",
                              padding: "1px 4px",
                              fontFamily: theme.fonts.sans,
                              marginBottom: "2px",
                            }}
                          >
                            {agDia.length}
                          </div>
                          {/* Nomes dos profissionais (sem repetir) */}
                          {Array.from(
                            new Set(
                              agDia.map((a) => a.profissionalNome.split(" ")[0])
                            )
                          ).map((nome, i) => (
                            <div
                              key={i}
                              style={{
                                fontSize: "11px",
                                color: theme.gray[500],
                                fontFamily: theme.fonts.sans,
                                textAlign: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {nome}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
